import { api, unwrapData } from '../../../app/store/api';
import { ApiResponse } from '../../../types/api-response.types';
import {
    TicketDepartmentType,
    TicketInfoType,
    TicketPriorityType,
    TicketGatewayType,
} from '../ticket.types';
import { immediateToast } from 'utils/toast';

const handledErrors = new Set<number>();

export const ticketApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getTicketsList: builder.query<
            any,
            { search?: object; page?: number; deleted?: boolean }
        >({
            transformResponse: unwrapData,
            query: ({ page = 1, search }) => ({
                url: 'tickets',
                method: 'GET',
                params: {
                    page,
                    ...(search ?? {}),
                },
            }),
            providesTags: ['TicketList'],
        }),

        getTicketGateways: builder.query<TicketGatewayType[], void>({
            transformResponse: unwrapData,
            query: () => ({
                url: 'tickets/gateways',
                method: 'GET',
                cache: 'no-store',
            }),
        }),

        getTicket: builder.query<TicketInfoType, number>({
            transformResponse: unwrapData,
            query: (id) => {
                return {
                    url: `tickets/${id}`,
                    method: 'GET',
                };
            },
            onQueryStarted: async (id, { queryFulfilled, dispatch }) => {
                try {
                    await queryFulfilled;
                    handledErrors.delete(id);
                } catch (err: any) {
                    const status = Number(err?.error?.status ?? 0);
                    if (status === 403 || status === 404) return;

                    if (!handledErrors.has(id)) {
                        handledErrors.add(id);
                        immediateToast('error', {
                            message:
                                err?.error?.data?.message ??
                                'خطایی در بارگیری تیکت اتفاق افتاده',
                            timeout: 3000,
                        });
                    }
                }
            },
        }),

        storeTicket: builder.mutation<
            unknown,
            {
                title: string;
                department: TicketDepartmentType;
                priority: TicketPriorityType;
                text: string;
                files: string[];
                gateway_id?: number | null;
            }
        >({
            transformResponse: unwrapData,
            query: (data) => ({
                url: `tickets`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result) =>
                result ? [{ type: 'TicketList' }] : [],
        }),

        sendMessage: builder.mutation<
            any,
            {
                id: number;
                message: {
                    text: string;
                    files: string[];
                    parent_id?: number | null;
                };
            }
        >({
            transformResponse: unwrapData,
            query: ({ id, message }) => ({
                url: `tickets/${id}/messages`,
                method: 'POST',
                body: message,
            }),
        }),

        ticketHappiness: builder.mutation<
            any,
            { id: string | number; satisfaction: 'happy' | 'unhappy' }
        >({
            transformResponse: unwrapData,
            query: ({ id, satisfaction }) => ({
                url: `tickets/${id}/satisfaction`,
                method: 'PUT',
                body: { satisfaction },
            }),
        }),
    }),
});

export const {
    useGetTicketsListQuery,
    useGetTicketGatewaysQuery,
    useGetTicketQuery,
    useStoreTicketMutation,
    useSendMessageMutation,
    useTicketHappinessMutation,
} = ticketApi;

export default ticketApi;
